// Browser-side equivalent of the old export-property-photos edge function.
// Returns a Blob; the download manager handles save-to-disk.

import JSZip from "jszip";
import type { BuildContext } from "@/features/exports/services/DownloadManagerContext";
import {
  fetchProperty,
  propertyFolderLabel,
  fetchAssetsForProperty,
  fetchAssetPhotos,
  fetchBytes,
  runWithConcurrency,
} from "./shared";
import { sanitize } from "@/features/exports/services/_helpers";

interface Args {
  projectId: string;
  propertyId: string;
}

const CONCURRENCY = 6;

export async function buildPropertyPhotosBundle(
  { propertyId }: Args,
  ctx: BuildContext,
): Promise<Blob> {
  const property = await fetchProperty(propertyId);
  if (!property) throw new Error("Property not found");
  const propertyLabel = propertyFolderLabel(property);

  ctx.setProgress(2, "Loading photo inventory…");

  const assets = await fetchAssetsForProperty(propertyId);
  const assetMap = new Map(assets.map((a) => [a.id, a]));
  const assetPhotos = await fetchAssetPhotos(assets.map((a) => a.id));

  const total = assetPhotos.length;
  if (total === 0) {
    throw new Error("No photos found for this property.");
  }

  const zip = new JSZip();
  let processed = 0;
  const failures: string[] = [];

  const bump = () => {
    processed++;
    const pct = 5 + Math.floor((processed / total) * 90);
    ctx.setProgress(pct, `${processed} / ${total} photos`);
  };

  const tasks: Array<() => Promise<void>> = [];

  for (const photo of assetPhotos) {
    tasks.push(async () => {
      if (ctx.signal.aborted) return;
      if (photo.file_path) {
        const asset = assetMap.get(photo.asset_id);
        const assetLabel = sanitize(asset?.name || photo.asset_id);
        const cat = sanitize(photo.category || "general");
        const buf = await fetchBytes(photo.file_path, {
          bucket: "asset-documents",
          signal: ctx.signal,
        });
        if (buf) {
          const folder = `${propertyLabel}/assets/${assetLabel}/${cat}`;
          const name = sanitize(photo.document_name || photo.id) + ".jpg";
          zip.file(`${folder}/${name}`, buf);
        } else {
          failures.push(`asset photo ${photo.id}`);
        }
      }
      bump();
    });
  }

  await runWithConcurrency(tasks, CONCURRENCY, ctx.signal);
  if (ctx.signal.aborted) throw new Error("Cancelled");

  if (failures.length > 0) {
    zip.file(
      `${propertyLabel}/MANIFEST.txt`,
      [`Photos that failed to download (${failures.length}):`, ...failures.map((s) => `  ${s}`)].join("\n"),
    );
  }

  ctx.setProgress(96, "Compressing…");
  // JPEGs are already compressed — STORE avoids burning CPU for no size win.
  const blob = await zip.generateAsync({ type: "blob", compression: "STORE" });
  ctx.setProgress(100, "Done");
  return blob;
}
